/** Benutzerverwaltung — karta v Profilu, jen pro partner adminy.
 *  Vše přes edge routy /partner-portal/portal/users* (service role řeší
 *  auth.users e-maily; RLS by partnerovi cizí řádky stejně nedala). */

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { authedFetch } from "@/lib/auth";
import { EDGE_FUNCTION_BASE } from "@/lib/supabase";
import { fmtDateShort } from "@/lib/format";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";

interface PortalUserRow {
  partner_user_id: string;
  auth_user_id: string;
  email: string | null;
  role: "admin" | "member";
  portal_type: string;
  invited_at: string;
  first_login_at: string | null;
  last_login_at: string | null;
  revoked_at: string | null;
}

interface UsersResponse {
  ok: boolean;
  users: PortalUserRow[];
  self_partner_user_id: string;
}

export function AccountUsersCard() {
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usersQ = useQuery({
    queryKey: queryKeys.portalUsers,
    queryFn: () => authedFetch<UsersResponse>(`${EDGE_FUNCTION_BASE}/partner-portal/portal/users`),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: queryKeys.portalUsers });

  const inviteM = useMutation({
    mutationFn: (payload: { email: string; role: "admin" | "member" }) =>
      authedFetch<{ ok: boolean; email: string; emailed: boolean }>(
        `${EDGE_FUNCTION_BASE}/partner-portal/portal/users/invite`,
        { method: "POST", body: JSON.stringify(payload) },
      ),
    onSuccess: (resp) => {
      setError(null);
      setNotice(resp.emailed ? t.account.inviteOkMailed(resp.email) : t.account.inviteOkNoMail);
      setInviteEmail("");
      setInviteRole("member");
      refresh();
    },
    onError: (err) => {
      setNotice(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const revokeM = useMutation({
    mutationFn: (partnerUserId: string) =>
      authedFetch(`${EDGE_FUNCTION_BASE}/partner-portal/portal/users/revoke`, {
        method: "POST",
        body: JSON.stringify({ partner_user_id: partnerUserId }),
      }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  const roleM = useMutation({
    mutationFn: (payload: { partner_user_id: string; role: "admin" | "member" }) =>
      authedFetch(`${EDGE_FUNCTION_BASE}/partner-portal/portal/users/set-role`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  const busy = inviteM.isPending || revokeM.isPending || roleM.isPending;
  const data = usersQ.data;
  const users = data?.users || [];
  const selfId = data?.self_partner_user_id;

  return (
    <div className="card" style={{ padding: 20 }}>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 6,
        }}
      >
        {t.account.usersTitle}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 14 }}>
        {t.account.usersIntro}
      </div>

      {usersQ.isLoading ? (
        <div style={{ padding: 16, color: "var(--text-3)", fontSize: 13 }}>{t.loading}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {users.map((u) => {
            const active = u.revoked_at === null;
            const isSelf = u.partner_user_id === selfId;
            return (
              <UserRow key={u.partner_user_id}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: active ? "var(--text)" : "var(--text-3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.email || "—"}
                    {isSelf && (
                      <span style={{ color: "var(--text-3)", marginLeft: 6, fontSize: 12 }}>
                        ({t.account.you})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>
                    {active
                      ? `${t.account.statusActive} · ${t.account.invitedAt} ${fmtDateShort(u.invited_at)} · ${t.account.lastLogin} ${u.last_login_at ? fmtDateShort(u.last_login_at) : t.account.never}`
                      : `${t.account.statusRevoked} ${fmtDateShort(u.revoked_at)}`}
                  </div>
                </div>
                {active ? (
                  <select
                    className="input"
                    value={u.role}
                    disabled={busy || isSelf}
                    onChange={(e) =>
                      roleM.mutate({
                        partner_user_id: u.partner_user_id,
                        role: e.target.value as "admin" | "member",
                      })
                    }
                    style={{ height: 30, fontSize: 12, width: 140 }}
                    aria-label={t.account.inviteRole}
                  >
                    <option value="admin">{t.account.roleAdmin}</option>
                    <option value="member">{t.account.roleMember}</option>
                  </select>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                    {u.role === "admin" ? t.account.roleAdmin : t.account.roleMember}
                  </span>
                )}
                {active && !isSelf && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      if (confirm(t.account.revokeConfirm(u.email || "—"))) {
                        revokeM.mutate(u.partner_user_id);
                      }
                    }}
                  >
                    <Ic.Close size={13} /> {t.account.revoke}
                  </Button>
                )}
              </UserRow>
            );
          })}

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              marginTop: 10,
              paddingTop: 14,
              borderTop: "1px solid var(--border)",
            }}
          >
            <input
              className="input"
              type="email"
              placeholder={t.account.inviteEmailPlaceholder}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              style={{ height: 34, fontSize: 13, flex: 1, minWidth: 180 }}
              aria-label={t.account.inviteTitle}
            />
            <select
              className="input"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as "admin" | "member")}
              style={{ height: 34, fontSize: 13, width: 150 }}
              aria-label={t.account.inviteRole}
            >
              <option value="member">{t.account.roleMember}</option>
              <option value="admin">{t.account.roleAdmin}</option>
            </select>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || !inviteEmail.trim()}
              onClick={() => inviteM.mutate({ email: inviteEmail.trim(), role: inviteRole })}
            >
              {inviteM.isPending ? (
                <Ic.Loader size={14} className="animate-spin" />
              ) : (
                <Ic.Plus size={14} />
              )}
              {t.account.invite}
            </Button>
          </div>

          {notice && (
            <div style={{ fontSize: 12.5, color: "var(--text-2, var(--text))", marginTop: 4 }}>
              {notice}
            </div>
          )}
          {error && (
            <div
              role="alert"
              style={{
                padding: 10,
                fontSize: 12.5,
                color: "var(--red)",
                background: "var(--red-12)",
                border: "1px solid rgba(248,113,113,0.22)",
                borderRadius: "var(--r-md)",
                marginTop: 4,
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserRow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        border: "1px solid var(--border)",
        borderRadius: "var(--r-md)",
      }}
    >
      {children}
    </div>
  );
}
